import { CITY } from '@city'
import type { PermissionStatus } from '@metro/location'
import type { MetroMapEvent } from './metroMapStore'
import type { SelectedStationSnapshot } from '../interaction/selectedStationSnapshot'

/**
 * Slug of the launch-time default map centre when geolocation is
 * unavailable — the city's `launchStationSlug` (London: King's Cross St.
 * Pancras, all 7 cluster records share it; NYC: Times Sq-42 St).
 */
export const LAUNCH_STATION_SLUG = CITY.launchStationSlug

/**
 * Host-side dependencies for the launch-time geolocation behavior
 * (TASK-391 §3.2). Injected so the launch contract is unit-testable.
 */
export interface LaunchGeolocationDeps {
  /** Reads the OS permission status WITHOUT prompting. */
  getPermission: () => Promise<PermissionStatus>
  /** Resolves the user's nearest station as a snapshot, or `null`. */
  resolveNearestSnapshot: () => Promise<SelectedStationSnapshot | null>
  /** Centers the map camera on a station. */
  focusOn: (snapshot: { canvasX: number; canvasY: number }) => void
  /** Centers the map camera on a station resolved by its URL slug. */
  focusOnStation: (slug: string) => void
  /** Dispatches an event into the MetroMap store. */
  dispatchMap: (event: MetroMapEvent) => void
}

/**
 * Runs the §3.2 app-launch geolocation behavior. Permission is **not**
 * requested at launch.
 *
 * - `granted` → take one fix → resolve nearest → focus + set
 *   `from = nearest` via `RouteFromSelectedByGeolocation` (which
 *   overrides any localStorage-hydrated `from` and leaves
 *   `fromWasUserModified` false). Falls back to King's Cross when no
 *   fix / station resolves.
 * - any other status → center on King's Cross St. Pancras, `from` stays
 *   empty.
 *
 * Mount AFTER `RouteSelectionUrlSync` + `RouteSelectionLocalStorageSync`
 * have hydrated so the geolocation snap deterministically wins.
 */
export async function runLaunchGeolocation(deps: LaunchGeolocationDeps): Promise<void> {
  const permission = await deps.getPermission()

  if (permission === 'granted') {
    const nearest = await deps.resolveNearestSnapshot()
    if (nearest !== null) {
      deps.focusOn(nearest)
      deps.dispatchMap({ kind: 'RouteFromSelectedByGeolocation', station: nearest })
      return
    }
  }

  // notDetermined / denied / restricted — or granted with no usable fix.
  deps.focusOnStation(LAUNCH_STATION_SLUG)
}
